"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var client_instance_id_1 = require("@services/instance-context/client-instance-id");
var environment_1 = require("@shared/types/environment");
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
var moment_1 = __importDefault(require("moment"));
var SECOND = 1000;
var MINUTE = SECOND * 60;
var HOUR = MINUTE * 60;
var DAY = HOUR * 24;
var WEEK = DAY * 7;
var MONTH = DAY * 30;
// a light-blue. It' the background color when highlighting the text in the status bar
var SUCCESSFUL_SAVE_COLOR = '#b1d4fc';
/**
 * Time until we assume a pending request failed.
 * If a requests exceeds this time, display an "error message" to the user.
 * But keep track of failing requests in case the failures recover.
 */
var PENDING_REQUEST_TTL_MS = 30 * SECOND;
/**
 * Time until we assume that a failed request cannot be recovered.
 * In this state, we stop keeping track of failed requests.
 */
var FAILED_REQUEST_TTL_MS = 60 * SECOND;
var SINGULAR_MAP = {
    N_MINUTES: 'ONE_MINUTE',
    N_HOURS: 'ONE_HOUR',
    N_DAYS: 'ONE_DAY',
    N_WEEKS: 'ONE_WEEK',
    N_MONTHS: 'ONE_MONTH'
};
var STATES = {
    /**
     * There are no pending requests and there are no failed requests
     */
    SAVED: 'saved',
    /**
     * There are no failed requests.
     * There are some requests that have not resolved or timed out yet.
     * The user sees "Saving..." in the upper right.
     * Keep track of pending requests, so we know when we can go to the SAVED or PENDING state.
     */
    PENDING: 'pending',
    /**
     * There are no unrecoverable failures.
     * But there are some recoverable failures.
     * The user sees "Error Saving Changes!" in the upper right.
     * Keep track of the failed records, just in case that all resolve in the future.
     */
    TEMP_FAILURE: 'temp_failed',
    /**
     * There are unrecoverable failures.
     * The user sees "Error Saving Changes!" in the upper right (same ui as TEMP_FAILURE).
     * Stop keeping track of failed requests and pending requests.
     * We can never leave this state without reloading the report.
     */
    PERM_FAILURE: 'perm_failure'
};
(function (module) {
    module.factory('pr.reportStatus', [
        '$filter',
        'rsEnvironment',
        'pr.statusBarCssManager',
        'pr.ttlCacheFactory',
        'pr.editAbilityManager',
        'pr.reportConflictModal',
        function (
        // @ts-ignore
        $filter, 
        // @ts-ignore
        env, statusBarCssManager, ttlCacheFactory, editAbilityManager, reportConflictModal) {
            var translate = $filter('translate');
            var lastUpdated = getLastUpdatedDate();
            var state = STATES.SAVED;
            var pendingRequests;
            var failedRequests;
            var isReportDeleted = false;
            init(PENDING_REQUEST_TTL_MS, FAILED_REQUEST_TTL_MS);
            return {
                savePending: savePending,
                saveFailed: saveFailed,
                saveSucceeded: saveSucceeded,
                modifySaveRequest: modifySaveRequest,
                getLastUpdatedDate: getLastUpdatedDate,
                updateLastUpdatedDate: function (newLastUpdatedDate) {
                    if (newLastUpdatedDate) {
                        lastUpdated = new Date(newLastUpdatedDate);
                    }
                },
                onReportDeletion: function () {
                    isReportDeleted = true;
                },
                anyFailedRequests: function () {
                    return lodash_1.default.includes([STATES.TEMP_FAILURE, STATES.PERM_FAILURE], getState());
                },
                getAbsoluteDateMessage: function () {
                    var formatted = moment_1.default(lastUpdated).format('YYYY-MM-DD HH:mm:ss');
                    return translate('PR.REPORT_SAVE.DATE', {
                        date: formatted
                    });
                },
                getRelativeDateMessage: function () {
                    var prevState = state;
                    state = getState();
                    if (lodash_1.default.includes([STATES.TEMP_FAILURE, STATES.PERM_FAILURE], state)) {
                        return translate('PR.REPORT_SAVE.FAILED');
                    }
                    else if (state === STATES.PENDING) {
                        return translate('PR.REPORT_SAVE.PENDING');
                    }
                    else if (prevState === STATES.PENDING) {
                        statusBarCssManager.highlight('2s', SUCCESSFUL_SAVE_COLOR);
                    }
                    // @ts-ignore
                    var timeSinceUpdate = new Date() - lastUpdated;
                    var translationKey;
                    var numUnits;
                    if (timeSinceUpdate < SECOND * 5) {
                        translationKey = 'JUST_NOW';
                    }
                    else if (timeSinceUpdate < MINUTE) {
                        translationKey = 'LESS_THAN_MINUTE';
                    }
                    else if (timeSinceUpdate < HOUR) {
                        translationKey = 'N_MINUTES';
                        numUnits = timeSinceUpdate / MINUTE;
                    }
                    else if (timeSinceUpdate < DAY) {
                        translationKey = 'N_HOURS';
                        numUnits = timeSinceUpdate / HOUR;
                    }
                    else if (timeSinceUpdate < WEEK) {
                        translationKey = 'N_DAYS';
                        numUnits = timeSinceUpdate / DAY;
                    }
                    else if (timeSinceUpdate < MONTH) {
                        translationKey = 'N_WEEKS';
                        numUnits = timeSinceUpdate / WEEK;
                    }
                    else {
                        translationKey = 'N_MONTHS';
                        numUnits = timeSinceUpdate / MONTH;
                    }
                    // @ts-ignore
                    numUnits = lodash_1.default.round(numUnits);
                    // @ts-ignore
                    if (numUnits === 1 && SINGULAR_MAP[translationKey]) {
                        // @ts-ignore
                        translationKey = SINGULAR_MAP[translationKey];
                    }
                    return translate('PR.REPORT_SAVE.' + translationKey, { numUnits: numUnits });
                },
                __TESTING__: {
                    // @ts-ignore
                    setLastUpdated: function (newLastUpdated) {
                        lastUpdated = newLastUpdated;
                    },
                    getLastUpdatedDate: getLastUpdatedDate,
                    initRequestCaches: init
                }
            };
            function init(pendingRequestTtlMs, failedRequestTtlMs) {
                /**
                 * Keep track of all of the pending requests.
                 * When all have been resolved, we have reached saved state.
                 * If any of these requests timeout, we have reached failed state.
                 */
                pendingRequests = ttlCacheFactory.createCache({
                    ttl: pendingRequestTtlMs || PENDING_REQUEST_TTL_MS,
                    // a pending request took too long. assume it failed
                    onExpiration: saveFailed
                });
                /**
                 * Keep track of all of the recoverable failed requests.
                 * When all have been resolved, we are no longer in failed state.
                 * If any of these requests timeout, we are in permanently failed state.
                 */
                failedRequests = ttlCacheFactory.createCache({
                    ttl: failedRequestTtlMs || FAILED_REQUEST_TTL_MS,
                    // assume this a failed request will never be resolved
                    onExpiration: permanentFailure
                });
                addWindowEventListener();
            }
            /**
             * Prompt the user if they navigate away from the page while there are unsaved changes.
             */
            function addWindowEventListener() {
                // E2E test brands should have window alerts disabled. Selenium does not have a good way to dismiss them.
                if (env.get(environment_1.ENV_VARS.DISABLE_WINDOW_ALERTS)) {
                    return;
                }
                window.addEventListener('beforeunload', function (e) {
                    if (isReportDeleted) {
                        // if the report was deleted, we don't care if saves are pending
                        return;
                    }
                    if (getState() === STATES.PENDING) {
                        // show a confirmation prompt before user leaves
                        e.preventDefault(); // needed for Firefox
                        e.returnValue = ''; // needed for Chrome
                    }
                });
            }
            function getState() {
                if (state === STATES.PERM_FAILURE) {
                    return STATES.PERM_FAILURE;
                }
                if (failedRequests.size()) {
                    return STATES.TEMP_FAILURE;
                }
                if (pendingRequests.size()) {
                    return STATES.PENDING;
                }
                return STATES.SAVED;
            }
            function saveSucceeded(id) {
                if (state === STATES.PERM_FAILURE) {
                    return;
                }
                pendingRequests.del(id);
                failedRequests.del(id);
            }
            function savePending(id) {
                if (state === STATES.PERM_FAILURE) {
                    return;
                }
                pendingRequests.set(id, true);
            }
            function saveFailed(id, errorMessage) {
                if (errorMessage === "CONFLICT" /* CONFLICT */) {
                    onReportConflict();
                }
                statusBarCssManager.reset();
                if (state === STATES.PERM_FAILURE) {
                    return;
                }
                pendingRequests.del(id);
                failedRequests.set(id, true);
            }
            /**
             * The client has an outdated version of the container.
             * Do not allow them to make any edits to the container without reloading.
             */
            function onReportConflict() {
                editAbilityManager.disable();
                reportConflictModal.show();
            }
            function permanentFailure() {
                if (state === STATES.PERM_FAILURE) {
                    return;
                }
                state = STATES.PERM_FAILURE;
                pendingRequests.clear();
                failedRequests.clear();
            }
            function getLastUpdatedDate() {
                var container = env.get('container') || {};
                var lastUpdatedDateStr = lodash_1.default.get(container, 'definition.lastModifiedDate');
                return new Date(lastUpdatedDateStr);
            }
            /**
             * Include client instance id as query parameters
             * so the backend can verify the client has an up-to-date report
             */
            function modifySaveRequest(config) {
                lodash_1.default.set(config, 'params.clientInstanceId', client_instance_id_1.getClientInstanceId());
            }
        }
    ]);
})(angular_1.module('qualtrics.pr'));
